import React from 'react';
import { Input, InputProps, TextArea, TextAreaProps, Form, Radio } from 'semantic-ui-react';
import { isNullOrUndefined } from '../../utils/siteFunctions';
import { SemanticDatepickerProps } from 'react-semantic-ui-datepickers/dist/types';
import DateInput from '../elements/DateInput';
import { Debtor, Address, EmailAddress, PhoneNumber } from '../../models/GeneralModels';
import { useSelector } from 'react-redux';

const EditDebtor = ({ value, onChange, headerText, showIsCompanyOption = true, minFieldsError = false }: EditDebtorProps) => {
	/// Global State
	const client = useSelector(state => state.ui.client);

	/// Events
	const handleChange = (e, data: InputProps | TextAreaProps | SemanticDatepickerProps) => {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(onChange)) {
			onChange?.({
				...(value as DebtorEditInfo),
				[data.name]: data.value as string
			});
		}
	};

	const handleAddressChange = (e, data: InputProps | TextAreaProps | SemanticDatepickerProps) => {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(onChange)) {
			onChange?.({
				...(value as DebtorEditInfo),
				addresses: [ {
					...value?.addresses?.[0] as Address,
					[data.name]: data.value as string
				} ]
			});
		}
	};

	const handleEmailAddressChange = (e, data: InputProps | TextAreaProps | SemanticDatepickerProps) => {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(onChange)) {
			onChange?.({
				...(value as DebtorEditInfo),
				emailAddresses: [ { 
					...value?.emailAddresses?.[0] as EmailAddress,
					[data.name]: data.value as string
				} ]
			});
		}
	};

	const handlePhoneNumberChange = (e, data: InputProps | TextAreaProps | SemanticDatepickerProps) => {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(onChange)) {
			onChange?.({
				...(value as DebtorEditInfo),
				phoneNumbers: value?.phoneNumbers?.map(x => x.phoneType !== data.name ? x : {
					...x,
					phoneNumber: data.value as string
				}) || []
			});
		}
	};

	const handleIsCompanyChange = (e, data) => {
		if (!isNullOrUndefined(value) && !isNullOrUndefined(onChange)) {
			onChange?.({
				...(value as DebtorEditInfo),
				isCompany: (data.value as number) === 1
			});
		}
	};

	/// Render
	return (
		<div style={{ maxWidth: '45em', width: '100%', display: 'inline-block', textAlign: 'left' }}>
			<div style={{
				display: 'grid',
				gridTemplateColumns: 'max-content max-content max-content',
				gridColumnGap: '1em',
				alignItems: 'baseline'
			}}>
				<h2>{headerText || 'Debtor Information'}</h2>
				{ showIsCompanyOption &&
					<>
						<Radio label="Individual" name="isCompany" value={0} checked={!value?.isCompany} onChange={handleIsCompanyChange} />
						<Radio label="Company" name="isCompany" value={1} checked={value?.isCompany} onChange={handleIsCompanyChange} />
					</>
				}
			</div>
			{isNullOrUndefined(value) || !value?.isCompany ? (
				<>
					<div className="justified-form-row">
						<div className="justified-form-field">
							First Name:
							<Input name="firstName" value={value?.firstName || ''} onChange={handleChange} error={minFieldsError} />
						</div>
						<div className="justified-form-field">
							Middle Name:
							<Input name="middleName" value={value?.middleName || ''} onChange={handleChange} />
						</div>
						<div className="justified-form-field">
							Last Name:
							<Input name="lastName" value={value?.lastName || ''} onChange={handleChange} error={minFieldsError} />
						</div>
					</div>
					<div className="justified-form-row">
						<div className="justified-form-field">
							Also Known As:
							<Input name="aka" value={value?.aka || ''} onChange={handleChange} />
						</div>
					</div>
				</>
			) : (
				<div className="justified-form-row">
					<div className="justified-form-field">
						Name:
						<Input name="companyName" value={value?.companyName} onChange={handleChange} />
					</div>
				</div>
			)}
			<div className="justified-form-row">
				<div className="justified-form-field">
					Address 1:
					<Input name="address" value={value?.addresses?.[0].address || ''} onChange={handleAddressChange} />
				</div>
			</div>
			<div className="justified-form-row">
				<div className="justified-form-field">
					Address 2:
					<Input name="addressTwo" value={value?.addresses?.[0].addressTwo || ''} onChange={handleAddressChange} />
				</div>
			</div>
			<div className="justified-form-row" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(5em, 1fr))' }}>
				<div className="justified-form-field" style={{ flexGrow: 3, flexShrink: 3 }}>
					City:
					<Input fluid name="city" value={value?.addresses?.[0].city || ''} onChange={handleAddressChange} />
				</div>
				<div className="justified-form-field">
					State:
					<Input fluid name="state" value={value?.addresses?.[0].state || ''} onChange={handleAddressChange} style={{ minWidth: '5em' }} />
				</div>
				<div className="justified-form-field" style={{ flexGrow: 2, flexShrink: 2 }}>
					Zip:
					<Input fluid name="zip" value={value?.addresses?.[0].zip || ''} onChange={handleAddressChange} />
				</div>
			</div>
			{client.showFieldsSettings.DebtorPhone &&
				<div className="justified-form-row">
					<div className="justified-form-field">
						Cell #:
						<Input name="cell" value={value?.phoneNumbers?.find(x => x.phoneType === 'cell')?.phoneNumber || ''} onChange={handlePhoneNumberChange} />
					</div>
					<div className="justified-form-field">
						Home #:
						<Input name="home" value={value?.phoneNumbers?.find(x => x.phoneType === 'home')?.phoneNumber || ''} onChange={handlePhoneNumberChange} />
					</div>
					<div className="justified-form-field">
						Fax #:
						<Input name="fax" value={value?.phoneNumbers?.find(x => x.phoneType === 'fax')?.phoneNumber || ''} onChange={handlePhoneNumberChange} />
					</div>
				</div>
			}
			<div className="justified-form-row">
				<div className="justified-form-field">
					SSN:
					<Input name="ssn" value={value?.ssn || ''} onChange={handleChange} />
				</div>
				<div className="justified-form-field">
					DOB:
					{/* <Input name="dob" value={value?.dob} onChange={handleChange} /> */}
					<DateInput
						name="dob"
						placeholder="DOB"
						value={value?.dob}
						onChange={handleChange}
					/>
				</div>
			</div>
			<div className="justified-form-row">
				{client.showFieldsSettings.DebtorEmail &&
					<div className="justified-form-field">
						E-mail:
						<Input name="emailAddress" value={value?.emailAddresses?.[0].emailAddress || ''} onChange={handleEmailAddressChange} />
					</div>
				}
				{client.showFieldsSettings.DebtorEmployer && (
					<div className="justified-form-field">
						Employer:
						<Input name="employerName" value={value?.employerName || ''} onChange={handleChange} />
					</div>
				)}
			</div>
			{/* <div className="justified-form-row">
				<div className="justified-form-field">
					Notes:
					<Form>
						<TextArea name="notes" value={value?.notes || ''} onChange={handleChange} />
					</Form>
				</div>
			</div> */}
		</div>
	);
};

export interface EditDebtorProps {
	value?: DebtorEditInfo;
	onChange?: (value: DebtorEditInfo) => any;
	headerText?: string;
	showIsCompanyOption?: boolean;
	minFieldsError?: boolean;
}

export interface DebtorEditInfo extends Debtor {
	notes: string
}

export const InitialDebtorInfo: DebtorEditInfo = {
	isCompany: false,
	firstName: undefined,
	middleName: undefined,
	lastName: undefined,
	aka: undefined,
	companyName: undefined,
	employerName: undefined,
	addresses: [ {
		address: '',
		addressTwo: '',
		city: '',
		state: '',
		zip: ''
	} ],
	phoneNumbers: [
		{
			phoneNumber: '',
			phoneType: 'cell'
		},
		{
			phoneNumber: '',
			phoneType: 'home'
		},
		{
			phoneNumber: '',
			phoneType: 'fax'
		}
	],
	ssn: undefined,
	dob: undefined,
	emailAddresses: [ { emailAddress: '' } ],
	notes: ''
};

export default EditDebtor;